import React, { useRef } from 'react';
import { flexRender } from '@tanstack/react-table';
import { useVirtual } from 'react-virtual';

import { SimpleLoading } from 'components/Loading';
import { LoadingState, SortableHeader, SortingIcon, StyledSortableIcon, TableContainer } from 'components/SectionTable';

import colors from 'styles/colors';
import useGeofencesTable from './hook';

const GeofenceTable = () => {
  const { table } = useGeofencesTable();

  const tableContainerRef = useRef(null);

  const { rows } = table.getRowModel();
  const rowVirtualizer = useVirtual({
    parentRef: tableContainerRef,
    size: rows.length,
    overscan: 4,
  });
  const { virtualItems, totalSize } = rowVirtualizer;

  const paddingTop = virtualItems.length > 0 ? virtualItems?.[0]?.start || 0 : 0;
  const paddingBottom = virtualItems.length > 0 ? totalSize - (virtualItems?.[virtualItems.length - 1]?.end || 0) : 0;

  const showSortingIcons = (canSort, isSorted) => {
    if (canSort && !isSorted) {
      return <StyledSortableIcon size={16} color={colors.lightGrayIcon} />;
    }
    if (isSorted) {
      return (
        <SortingIcon
          size={14}
          color={colors.fleetDarkGray}
          direction={
            {
              asc: SortingIcon.Direction.Up,
              desc: SortingIcon.Direction.Down,
            }[isSorted]
          }
        />
      );
    }
    return null;
  };

  return (
    <TableContainer ref={tableContainerRef}>
      {rows.length > 0 ? (
        <table className="table">
          <thead className="thead">
            {
              // Loop over the header rows
              table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const isSorted = header.column.getIsSorted();
                    const canSort = header.column.getCanSort();
                    return (
                      <SortableHeader
                        key={header.id}
                        $sortable={canSort}
                        style={{
                          width: header.getSize() === Number.MAX_SAFE_INTEGER ? undefined : header.getSize(),
                        }}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {header.isPlaceholder ? null : (
                          <div>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {showSortingIcons(canSort, isSorted)}
                          </div>
                        )}
                      </SortableHeader>
                    );
                  })}
                </tr>
              ))
            }
          </thead>
          <tbody className="tbody">
            {paddingTop > 0 && (
              <tr>
                <td style={{ height: `${paddingTop}px` }} />
              </tr>
            )}
            {virtualItems.map((virtualRow) => {
              const row = rows[virtualRow.index];
              return (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                  ))}
                </tr>
              );
            })}
            {paddingBottom > 0 && (
              <tr>
                <td style={{ height: `${paddingBottom}px` }} />
              </tr>
            )}
          </tbody>
        </table>
      ) : (
        <LoadingState>
          <SimpleLoading color={colors.fleetBlue} />
        </LoadingState>
      )}
    </TableContainer>
  );
};

export default GeofenceTable;
